import { createContext, useContext, useState, type ReactNode } from 'react'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'

export interface AlertOptions {
  title?: string
  variant?: 'info' | 'success' | 'warning' | 'error'
}

export interface ConfirmOptions {
  title?: string
  confirmLabel?: string
  cancelLabel?: string
  variant?: 'danger' | 'primary'
}

interface AlertContextValue {
  showAlert: (message: string, options?: AlertOptions) => Promise<void>
  showConfirm: (message: string, options?: ConfirmOptions) => Promise<boolean>
}

const AlertContext = createContext<AlertContextValue | undefined>(undefined)

export function AlertProvider({ children }: { children: ReactNode }) {
  // Alert State
  const [alertOpen, setAlertOpen] = useState(false)
  const [alertMessage, setAlertMessage] = useState('')
  const [alertOptions, setAlertOptions] = useState<AlertOptions>({})
  const [alertResolver, setAlertResolver] = useState<(() => void) | null>(null)

  // Confirm State
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmMessage, setConfirmMessage] = useState('')
  const [confirmOptions, setConfirmOptions] = useState<ConfirmOptions>({})
  const [confirmResolver, setConfirmResolver] = useState<((value: boolean) => void) | null>(null)

  const showAlert = (message: string, options?: AlertOptions) => {
    return new Promise<void>((resolve) => {
      setAlertMessage(message)
      setAlertOptions(options || { title: 'Notification' })
      setAlertResolver(() => resolve)
      setAlertOpen(true)
    })
  }

  const showConfirm = (message: string, options?: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      setConfirmMessage(message)
      setConfirmOptions(options || {})
      setConfirmResolver(() => resolve)
      setConfirmOpen(true)
    })
  }

  const handleAlertClose = () => {
    setAlertOpen(false)
    if (alertResolver) alertResolver()
  }

  const handleConfirmCancel = () => {
    setConfirmOpen(false)
    if (confirmResolver) confirmResolver(false)
  }

  const handleConfirmAccept = () => {
    setConfirmOpen(false)
    if (confirmResolver) confirmResolver(true)
  }

  return (
    <AlertContext.Provider value={{ showAlert, showConfirm }}>
      {children}
      
      {/* Alert Modal */}
      <Modal
        open={alertOpen}
        onClose={handleAlertClose}
        title={alertOptions.title || 'Notification'}
        size="sm"
        footer={
          <Button variant="primary" onClick={handleAlertClose}>
            OK
          </Button>
        }
      >
        <p style={{ color: 'var(--sz-text-2)', fontSize: '0.9375rem', lineHeight: 1.6 }}>
          {alertMessage}
        </p>
      </Modal>

      {/* Confirm Modal */}
      <ConfirmDialog
        open={confirmOpen}
        onClose={handleConfirmCancel}
        onConfirm={handleConfirmAccept}
        title={confirmOptions.title || 'Confirm Action'}
        message={confirmMessage}
        confirmLabel={confirmOptions.confirmLabel || 'Confirm'}
        cancelLabel={confirmOptions.cancelLabel || 'Cancel'}
        variant={confirmOptions.variant || 'danger'}
      />
    </AlertContext.Provider>
  )
}

export function useAlert() {
  const context = useContext(AlertContext)
  if (context === undefined) {
    throw new Error('useAlert must be used within an AlertProvider')
  }
  return context
}
